import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import AsyncDropdown, { PropsWithAsyncDropdown } from 'core/elements/dropdown/AsyncDropdown'
import { DropdownItemSpec } from 'core/elements/dropdown/DropdownCommonProps'
import Tooltip, { TooltipProps } from 'core/elements/tooltip/Tooltip'
import useListAction from 'core/hooks/useListAction'
import { isRestrictedSsu } from 'core/utils/rbac'
import { listClusterHostGroups, listClusterHostGroupsAllTenants } from './actions'
import { clusterHostGroupsAllTenantsSelector, clusterHostGroupsSelector } from './selectors'

interface Props {
  label?: string
  width?: number
  fullWidth?: boolean
  tooltip?: string
  tooltipProps?: TooltipProps
  includeNone?: boolean
  disableClusterWithoutHosts?: boolean
  autoSelectFirstValue?: boolean
  filterFn?: (item: any) => boolean
  // Replaces the default option mapper entirely — disableClusterWithoutHosts is NOT
  // applied when this is passed.
  optionMapFn?: (value: any) => DropdownItemSpec<string>
  // PCD-11077: infra-only flows (host join/assignment, host aggregates, stretched clusters) must
  // offer every cluster, including tenant-dedicated ones, so they read the all-tenants list.
  // Tenant-facing flows (Create/Clone VM, k8s placement) keep the backend's filtered default.
  allTenants?: boolean
}

export default function ClusterHostGroupsPicklist({
  width = 180,
  fullWidth = false,
  label,
  tooltip = '',
  tooltipProps,
  includeNone = false,
  disableClusterWithoutHosts = false,
  autoSelectFirstValue = false,
  filterFn,
  optionMapFn,
  allTenants = false,
  value,
  onChange,
  ...rest
}: PropsWithAsyncDropdown<Props>) {
  // CHGP-FR-001: cluster options are sourced from clusterHostGroupsSelector
  const { loading: loadingFiltered } = useListAction(listClusterHostGroups, {
    preRequisites: !allTenants,
  })
  const { loading: loadingAllTenants } = useListAction(listClusterHostGroupsAllTenants, {
    preRequisites: allTenants,
  })
  const loading = allTenants ? loadingAllTenants : loadingFiltered
  const clusterHostGroups = useSelector(
    allTenants ? clusterHostGroupsAllTenantsSelector : clusterHostGroupsSelector,
  )

  const items = useMemo(() => {
    // For restricted SSUs, filter out any cluster starting with 'internal' string
    const accessibleHostGroups = isRestrictedSsu()
      ? clusterHostGroups.filter((cluster) => !cluster?.name?.startsWith('internal'))
      : clusterHostGroups
    const filteredHostGroups = filterFn
      ? accessibleHostGroups.filter(filterFn)
      : accessibleHostGroups
    const mapFn = optionMapFn
      ? optionMapFn
      : (clusterHostGroup) => {
          // CHGP-FR-002: grey out clusters with no hosts
          const noHosts = disableClusterWithoutHosts && clusterHostGroup.hosts?.length === 0

          return {
            label: `${clusterHostGroup.name}${noHosts ? ' (No Hosts Assigned)' : ''}`,
            value: clusterHostGroup.name,
            disabled: noHosts,
          }
        }
    // CHGP-FR-004: sort disabled (no-hosts) options to the end
    const options = filteredHostGroups.map(mapFn).sort((a, b) => {
      // Sort disabled items to the end
      if (a.disabled && !b.disabled) return 1
      if (!a.disabled && b.disabled) return -1
      return 0
    })
    if (!includeNone) {
      return options
    }

    return [
      {
        label: '-- None --',
        value: '',
      },
      ...options,
    ]
  }, [clusterHostGroups, includeNone, disableClusterWithoutHosts, filterFn, optionMapFn])

  // Auto select first value when enabled
  useEffect(() => {
    if (!autoSelectFirstValue) return

    if (!loading && items.length > 0 && !value && onChange) {
      const firstEnabled = items.find((item) => !item.disabled)
      if (firstEnabled) {
        onChange(firstEnabled.value)
      }
    }
  }, [items, loading, autoSelectFirstValue, value, onChange])

  return (
    <Tooltip message={tooltip} {...tooltipProps}>
      <AsyncDropdown
        name={label}
        label={label}
        items={items}
        formField
        loading={loading}
        width={fullWidth ? undefined : width}
        value={value}
        onChange={onChange}
        {...rest}
      />
    </Tooltip>
  )
}

// PCD-11077: for Infrastructure screens (only reachable by canManageInfrastructure roles).
export function AllTenantsClusterHostGroupsPicklist(props: PropsWithAsyncDropdown<Props>) {
  return <ClusterHostGroupsPicklist {...props} allTenants />
}
